import axios from 'axios';
import config from './config';
import { ChessGame, ChessPlayerPoints } from '../types/chess';

// Public service that doesn't require authentication
// Uses axios directly instead of the authenticated http client

const baseURL = `${config.baseURL}/api`;

/** Live arena state from backend (same as WebSocket state, for public polling). */
export interface PublicLiveArenaState {
  fen: string;
  moves: string[];
  gameStatus: 'waiting' | 'playing' | 'finished';
  currentTurn: 'white' | 'black';
  whiteTimeRemaining: number;
  blackTimeRemaining: number;
  timerStarted: boolean;
  whiteSeat: { oderId: string; userName: string; userRating: number } | null;
  blackSeat: { oderId: string; userName: string; userRating: number } | null;
  waitlist: Array<{ id: string; name: string; rating: number }>;
}

/** Response from GET /chess-arenas/public/room/:roomId (nest-brc) */
export interface PublicArenaByRoomPayload {
  roomName: string;
  arena: { id: string; name?: string; [k: string]: any };
  leaderboard: Array<{ id?: string; totalPoints?: number; wins?: number; draws?: number; losses?: number; player?: { name?: string; email?: string } }>;
  games: Array<{ id?: string; result?: string; moves?: string[]; createdAt?: string; whitePlayer?: { name?: string }; blackPlayer?: { name?: string }; status?: string }>;
  liveState?: PublicLiveArenaState | null;
}

class PublicArenaService {
  /**
   * Fetch public arena data by BRC room id (chess-arenas backend).
   * Anyone can view: room name, arena, leaderboard, recent games. No auth.
   */
  async getPublicArenaByRoomId(roomId: string): Promise<PublicArenaByRoomPayload> {
    const { data } = await axios.get<PublicArenaByRoomPayload>(`${baseURL}/chess-arenas/public/room/${roomId}`);
    return data;
  }

  /** Legacy: room-based rankings/games (non-arena). Prefer getPublicArenaByRoomId for arena rooms. */
  async getPublicRoomData(roomId: string): Promise<{
    room: any;
    leaderboard: ChessPlayerPoints[];
    games: ChessGame[];
  }> {
    try {
      const [leaderboardResponse, gamesResponse] = await Promise.allSettled([
        axios.get(`${baseURL}/rooms/${roomId}/rankings`),
        axios.get(`${baseURL}/chess-games/room/${roomId}`),
      ]);

      const leaderboard = leaderboardResponse.status === 'fulfilled' ? (leaderboardResponse.value.data || []) : [];
      const games = gamesResponse.status === 'fulfilled' ? (gamesResponse.value.data || []) : [];

      return {
        room: null,
        leaderboard: Array.isArray(leaderboard) ? leaderboard : [],
        games: Array.isArray(games) ? games : [],
      };
    } catch (error: any) {
      console.error('Error fetching public arena data:', error);
      return { room: null, leaderboard: [], games: [] };
    }
  }

  async getPublicRoomLeaderboard(roomId: string): Promise<ChessPlayerPoints[]> {
    try {
      const response = await axios.get(`${baseURL}/rooms/${roomId}/rankings`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching public leaderboard:', error);
      return [];
    }
  }

  async getPublicRoomGames(roomId: string): Promise<ChessGame[]> {
    try {
      const response = await axios.get(`${baseURL}/chess-games/room/${roomId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching public games:', error);
      return [];
    }
  }
}

const publicArenaService = new PublicArenaService();
export default publicArenaService;

